import { Dispatch, SetStateAction, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';

import PreviousReports from 'components/reports/PreviousReports';
import { LabReport } from 'services/interpret-lab-report/type';
import { downloadFile } from 'lib/utils';
import { Button } from 'components/ui/button';
import Loader from 'components/Loader';
import GenerateReport from './report/GenerateReport';
import useAuthStore from 'store/auth';
import ReportActions from 'components/reports/ReportActions';
import {
  useDeleteSymptomReports,
  useGetSymptomReportDetail,
} from 'services/symptom-checker/hooks';
import NoData from 'components/NoData';
import ReportDetailInfo from './ReportDetailInfo';

interface MobileReportProps {
  reportData: LabReport[];
  setReportData: Dispatch<SetStateAction<LabReport[]>>;
  scrollViewElement: React.ReactNode;
  isLoading: boolean;
}

export default function MobileReport({
  reportData,
  setReportData,
  scrollViewElement,
  isLoading,
}: MobileReportProps) {
  const [selectedTokenId, setSelectedTokenId] = useState<string>('');

  if (selectedTokenId) {
    return (
      <ReportDetail
        tokenId={selectedTokenId}
        onBack={() => setSelectedTokenId('')}
      />
    );
  }

  return (
    <div className='col-span-4 px-6 pt-4 lg:hidden'>
      <div className='space-y-4'>
        <GenerateReport />

        <PreviousReports
          reportData={reportData}
          tokenId={selectedTokenId}
          setTokenId={setSelectedTokenId}
          setReportData={setReportData}
          scrollViewElement={scrollViewElement}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function ReportDetail({
  tokenId,
  onBack,
}: {
  tokenId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const {
    data: reportDetail,
    isFetching,
    isError,
  } = useGetSymptomReportDetail({
    tokenId: tokenId,
  });

  const { mutate: deleteReport, isPending: isDeleting } =
    useDeleteSymptomReports({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['inifinite-symptom-reports', profileId],
        });
        toast.success(t('report_deleted_msg'));
        onBack();
      },
      onError: (error) => {
        //  errorToast(error.message);
        toast.error(error.message);
      },
    });

  const onDownload = async (url: string | undefined) => {
    setIsDownloading(true);
    try {
      await downloadFile(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const onDelete = () => {
    deleteReport([tokenId]);
  };

  if (isFetching) {
    return <Loader />;
  }

  if (isError || !reportDetail?.length) {
    return (
      <div className='h-[calc(100vh-12rem)]'>
        <NoData className='border-none' message={t('no_report_data')} />
      </div>
    );
  }

  return (
    <div className='px-6 pt-4 sm:px-14 lg:hidden'>
      <div className='flex justify-end'>
        <Button
          variant='ghost'
          className='text-sm text-blue-500 hover:text-blue-500 h-fit'
          onClick={onBack}
        >
          <ChevronLeft className='w-4 h-4 mr-2' /> {t('previous_reports')}
        </Button>
      </div>

      <ReportActions
        content={reportDetail?.[0]?.report_link || ''}
        isDownloading={isDownloading}
        isDeleting={isDeleting}
        onDelete={onDelete}
        onDownload={() => onDownload(reportDetail?.[0]?.report_link)}
        disabled={!reportDetail?.length}
      />

      <div className='h-4' />

      <ReportDetailInfo detail={reportDetail?.[0]} />
    </div>
  );
}
